import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, RefreshCw, CreditCard, Wallet } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { usePrivy, useSolanaWallets } from "@privy-io/react-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Connection, Transaction } from "@solana/web3.js";
import { rpcUrl } from "@/components/account/credits/Credits";
import { initPayment, confirmPayment } from "@/components/web3/transactions";
import { creditQueries } from "@/api/credits";
import ProcessingModal from "@/components/ProcessingModal";
import { createPaymentIntent } from "@/api/stripe";
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StripeCheckoutModal } from "@/components/account/payments/StripeCheckoutModal";
import { apiClient } from "@/api/api";

const purchaseFormSchema = z.object({
  amount: z.number().min(20, "Minimum purchase is $20 (2,000 credits)"),
  currency: z.enum(["SOL", "CARD"]),
});

type PurchaseValues = z.infer<typeof purchaseFormSchema>;

export function PurchaseCreditsCard({ personaId }: { personaId: string }) {
  const { login, authenticated, connectWallet } = usePrivy();
  const queryClient = useQueryClient();
  const { wallets } = useSolanaWallets();
  const solWallet = wallets?.[0];

  // Track loading state for Stripe payment verification + 3s delay
  const [isRefetching, setIsRefetching] = useState(false);

  const purchaseForm = useForm<PurchaseValues>({
    resolver: zodResolver(purchaseFormSchema),
    defaultValues: {
      amount: 100,
      currency: "SOL", // default to SOL
    },
  });

  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();

  // Handles creation of Stripe PaymentIntent, then shows modal
  const stripeCheckoutMutation = useMutation({
    mutationFn: async (data: PurchaseValues) => {
      try {
        const { clientSecret } = await createPaymentIntent({
          amount: data.amount,
          persona_id: personaId,
        });
        setClientSecret(clientSecret);
        setIsStripeModalOpen(true);
      } catch (error: any) {
        console.error("Stripe payment intent error:", error);
        throw new Error(
          error?.response?.data?.detail || "Failed to create payment intent",
        );
      }
    },
    onError: (error: any) => {
      console.error("Stripe mutation error:", error);
      toast({
        variant: "destructive",
        description: error?.message || "Failed to initiate payment",
      });
    },
  });

  // Detect Stripe redirect success, then verify payment, show spinner 3s, refetch
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntentId = urlParams.get("payment_intent");
    const redirectStatus = urlParams.get("redirect_status");

    if (paymentIntentId && redirectStatus === "succeeded") {
      setIsRefetching(true);
      apiClient
        .post("/stripe/verify-payment", {
          payment_intent_id: paymentIntentId,
        })
        .then(() => {
          toast({
            title: "Payment Successful",
            description: "Your credits have been added to your account.",
          });
          // Wait 3 seconds, then invalidate the query
          setTimeout(() => {
            queryClient.invalidateQueries(
              creditQueries.agentBalance(personaId),
            );
            setIsRefetching(false);
          }, 3000);
        })
        .catch((error) => {
          console.error("Error verifying payment:", error);
          toast({
            variant: "destructive",
            description:
              "There was an issue verifying your payment. Please contact support.",
          });
          setIsRefetching(false);
        })
        .finally(() => {
          // Clean up URL
          window.history.replaceState({}, "", window.location.pathname);
        });
    }
  }, [personaId, queryClient]);

  // Handles crypto purchase (SOL)
  const purchaseMutation = useMutation({
    mutationFn: async (data: PurchaseValues) => {
      if (!solWallet || !personaId) {
        throw new Error("Wallet not connected or no persona selected");
      }

      // Step 1: Initialize payment and get partial signed transaction
      const initResult = await initPayment({
        personaId: personaId,
        userAddress: solWallet.address,
        currency: data.currency, // either "SOL" or "CARD", but we handle "SOL" here
        amountUsd: data.amount,
      });

      if (!initResult.payment_id || !initResult.metadata?.partial_signed_tx) {
        throw new Error("Invalid payment initialization response");
      }

      toast({ description: "Please approve the transaction in your wallet" });

      const connection = new Connection(rpcUrl);
      const txBytes = Uint8Array.from(
        atob(initResult.metadata?.partial_signed_tx),
        (c) => c.charCodeAt(0),
      );

      const transaction = Transaction.from(txBytes);

      // Step 2: Send transaction through wallet
      const txSignature = await solWallet.sendTransaction(
        transaction,
        connection,
      );

      // Step 3: Confirm payment with backend
      await confirmPayment({
        paymentId: initResult.payment_id,
        txSignature,
      });

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(creditQueries.agentBalance(personaId));
      toast({
        title: "Credits Purchased",
        description: "Credits have been successfully added to your account.",
      });
      purchaseForm.reset();
    },
    onError: (error: any) => {
      if (error.message.includes("rejected")) {
        toast({
          variant: "destructive",
          description: "Transaction was cancelled. Please try again.",
        });
      } else {
        toast({
          variant: "destructive",
          description: error?.message || "Failed to purchase credits",
        });
      }
    },
  });

  // Submit handler for the entire form
  const onPurchaseSubmit = (values: PurchaseValues) => {
    if (values.currency === "CARD") {
      stripeCheckoutMutation.mutate(values);
      return;
    }

    if (!authenticated) {
      login();
      return;
    }

    if (!solWallet) {
      connectWallet();
      return;
    }

    purchaseMutation.mutate(values);
  };

  return (
    <>
      {/* Show processing modal if either crypto payment is pending or we're waiting post-Stripe */}
      <ProcessingModal open={purchaseMutation.isPending || isRefetching} />

      {/* Stripe modal for card payments */}
      {clientSecret && (
        <StripeCheckoutModal
          open={isStripeModalOpen}
          onClose={() => {
            setIsStripeModalOpen(false);
            setClientSecret(undefined);
          }}
          clientSecret={clientSecret}
          amount={purchaseForm.watch("amount") || 0}
          personaId={personaId}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Purchase Credits
          </CardTitle>
          <CardDescription>
            Buy additional credits for your agent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            defaultValue="crypto"
            className="w-full"
            onValueChange={(value) => {
              const currency = value === "card" ? "CARD" : "SOL";
              purchaseForm.setValue("currency", currency);
            }}
          >
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="card" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Credit Card
              </TabsTrigger>
              <TabsTrigger value="crypto" className="flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                Solana Wallet
              </TabsTrigger>
            </TabsList>

            <Form {...purchaseForm}>
              <form
                onSubmit={purchaseForm.handleSubmit(onPurchaseSubmit)}
                className="space-y-6"
              >
                <div className="grid grid-cols-2 gap-4">
                  {/* Crypto tab content: only SOL in the dropdown */}
                  <TabsContent value="crypto">
                    <FormField
                      control={purchaseForm.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Crypto Currency</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              purchaseForm.setValue("currency", value as "SOL");
                            }}
                            defaultValue="SOL"
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="SOL">SOL</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>

                  {/* Card tab content */}
                  <TabsContent value="card" className="col-span-2" />

                  {/* USD Amount Input */}
                  <FormField
                    control={purchaseForm.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Amount (USD)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter USD amount"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Preview of credits to receive */}
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-muted-foreground">
                      Credits to receive:
                    </span>
                    <span className="font-mono font-medium">
                      {(purchaseForm.watch("amount") || 0) * 100} credits
                    </span>
                  </div>
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    purchaseMutation.isPending ||
                    stripeCheckoutMutation.isPending
                  }
                >
                  {(purchaseMutation.isPending ||
                    stripeCheckoutMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {purchaseForm.watch("currency") === "CARD"
                    ? "Purchase with Credit Card"
                    : !authenticated
                      ? "Connect Wallet"
                      : !solWallet
                        ? "Select Wallet"
                        : "Purchase with Crypto"}
                </Button>
              </form>
            </Form>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}
